// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import getGlobalProps from '../../helpers/constants/global-props';

import ProgressBar from './ProgressBar/ProgressBar';
import ProgressLegend from './ProgressLegend/ProgressLegend';
import ProgressLegendItem from './ProgressLegend/ProgressLegendItem/ProgressLegendItem';
import getBarData from './helpers/getBarData';
import getDataLegends from './helpers/getDataLegends';
import getMaximumLegend from './helpers/getMaximumLegend';
import normalizeData from './helpers/normalizeData';

import { CurrencyProps } from '../Currency/Currency';
import { ProgressWrapper } from './styles';

export type CurrencyFormatType = Pick<CurrencyProps, 'precision' | 'symbol'>;

export type LegendFormatType = 'money' | 'number' | string | CurrencyFormatType;

export interface ProgressNodeDatum {
  value: number;
  color: string;
  legend: ReactNode;
}

export interface ProgressLabelDatum {
  value: number;
  label?: string;
  color: string;
}

export interface ProgressLabelMaximum {
  value: number;
  label?: string;
}

export interface ProgressNodeMaximum {
  value: number;
  legend: ReactNode;
}

export type SizeType = 'small' | 'large';

export interface ProgressProps {
  /** A number (for single-value progress bars), or an array of objects
   * describing multiple values (for multi-value progress bars). If using the `showLegend` option,
   * the array form can supply either a string ("label"), or an entire Node ("legend") for each value. */
  data: number | ProgressNodeDatum[] | ProgressLabelDatum[];
  /** A single number for simple progress bars. If using the `showLegend` option, can also be an
   * object containing the value and a string label for the maximum,
   * or an object containing the value and an entire Node ("legend").
   */
  maximum: number | ProgressNodeMaximum | ProgressLabelMaximum;
  /** Size of the progress bar (small or large) - defaults to small */
  size?: SizeType;
  /** Whether or not to display a built-in legend for this progress bar */
  showLegend?: boolean;
  /** How to format numbers on auto-generated legend entries. Presets include "money" and "number". */
  legendFormat?: LegendFormatType;
  /** Animates progress bar on data change */
  animated?: boolean;
  /** Adds shimmer effect for loading states while performing requests  */
  loading?: boolean;
  /** Removes built-in spacing */
  trim?: boolean;
  /** Allows screen readers to describe the content */
  'aria-label'?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Graphically displays numeric values as percentage of a total, as in a progress bar
 * @categories accessory
 * @group Progress
 * @aka bar
 */
function Progress(props: ProgressProps) {
  const {
    size = 'small',
    showLegend,
    data,
    maximum,
    legendFormat = 'number',
    trim,
    loading,
    animated,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    ...otherProps
  } = props;

  const normalizedData = normalizeData(data);
  const dataLegends = getDataLegends(normalizedData);
  const barData = getBarData(normalizedData, maximum);

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        progressTrim: trim,
        animated,
      })}
    >
      <ProgressWrapper data-testid={dataTestId} {...globalProps} {...otherProps}>
        <ProgressBar data={barData} size={size} loading={loading} aria-label={ariaLabel} />
        {showLegend && (
          <ProgressLegend
            size={size}
            dataLegends={dataLegends}
            maximumLegend={getMaximumLegend(maximum)}
            legendFormat={legendFormat}
          />
        )}
      </ProgressWrapper>
    </ThemeProvider>
  );
}

Progress.LegendItem = ProgressLegendItem;

Progress.defaultProps = {
  size: 'small',
  showLegend: false,
  legendFormat: 'number',
  trim: false,
  animated: true,
  loading: undefined,
};

export { ProgressLegendItemProps } from './ProgressLegend/ProgressLegendItem/ProgressLegendItem';

Progress.displayName = 'Progress';

export default Progress;
